import { ObservableSet, makeAutoObservable } from 'mobx';
import type { AccountKey, MailboxInfo } from 'owa-client-types';
import {
    getIndexerValueForMailboxInfo,
    getAccountKeyForMailboxInfo,
    defaultAccountKey,
} from 'owa-client-types';
import { createStore } from 'owa-satcheljs';
import type { NotificationsFilterValues } from '../components/NotificationsFilter';
import type { ActivityFeedItem } from '../service/ActivityFeedItem';
import type { FeedAction } from './feedAction';
import { feedAction } from './feedAction';
import type { TraceErrorObject } from 'owa-trace';

export interface ActivityFeedStore {
    feedAction: FeedAction;
    isActivityFeedOpen: boolean;
    isActivityFeedSettingOpen: boolean;
    isOpx: boolean;
    accountItems: Map<AccountKey, ActivityFeedAccountStore>; // Account key to store of feed items for the account
    focusedItemId: string;
    notificationsDisabled: boolean;
    currentAccountId: AccountKey;
}

export interface ActivityFeedAccountStore {
    firstFetchComplete: boolean;
    currentlyRenderedItemIds: ObservableSet<string>; // Filtered list of feed item IDs that are visible
    items: Map<string, ActivityFeedItem>; // Full feed item info
    unseenActivityCount: number;
    loadSucceeded: boolean;
    batchCount: number;
    lastSeenTimestamp: string | null;
    canShowNoNewNotificationsUI: boolean;
    cardFilters: ObservableSet<NotificationsFilterValues>;
}

export const getActivityFeedStore: () => ActivityFeedStore = createStore<ActivityFeedStore>(
    'activityFeedStore',
    {
        /* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
         * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
         *	> Function feedAction called at module scope is not allowed. This may cause unwanted side effects. */
        feedAction: feedAction(),
        isActivityFeedOpen: false,
        isActivityFeedSettingOpen: false,
        isOpx: false,
        accountItems: new Map<AccountKey, ActivityFeedAccountStore>(),
        focusedItemId: '',
        notificationsDisabled: false,
        currentAccountId: defaultAccountKey,
    }
);

export const createFeedAccountStore = (accountStoreId: string): ActivityFeedAccountStore => {
    /* eslint-disable-next-line owa-custom-rules/invoke-only-in-module-scope -- (https://aka.ms/OWALintWiki)
     * Baseline. DO NOT COPY AND PASTE!, this function can only be called in the module scope.
     *	> Function should only be invoked in module scope */
    const storeInitializer = createStore<ActivityFeedAccountStore>(accountStoreId, {
        firstFetchComplete: false,
        currentlyRenderedItemIds: new ObservableSet<string>(),
        items: new Map<string, ActivityFeedItem>(),
        unseenActivityCount: 0,
        loadSucceeded: false,
        batchCount: 1,
        lastSeenTimestamp: null,
        canShowNoNewNotificationsUI: true,
        cardFilters: new ObservableSet<NotificationsFilterValues>(),
    });
    return storeInitializer();
};

export const getActivityFeedAccountStore = (mailboxInfo: MailboxInfo): ActivityFeedAccountStore => {
    const mailboxIndex = getIndexerValueForMailboxInfo(mailboxInfo);
    const accountStore = getActivityFeedStore().accountItems.get(mailboxIndex);
    if (!accountStore) {
        const err: TraceErrorObject = new Error(
            'getActivityFeedAccountStore is called for an account where initializeNotificationsPaneForAccount has not been called.'
        );
        err.additionalInfo = { diagnosticMessage: mailboxInfo.mailboxSmtpAddress };
        throw err;
    }
    return accountStore;
};

export const getActivityFeedAccountStores = (
    mailboxInfos: MailboxInfo[]
): {
    accountStore: ActivityFeedAccountStore;
    accountKey: AccountKey;
}[] => {
    return mailboxInfos.map(mailboxInfo => {
        const mailboxIndex = getIndexerValueForMailboxInfo(mailboxInfo);
        const accountStore = getActivityFeedStore().accountItems.get(mailboxIndex);
        if (!accountStore) {
            const err: TraceErrorObject = new Error(
                'getActivityFeedAccountStores is called for an account where initializeNotificationsPaneForAccount has not been called.'
            );
            err.additionalInfo = { diagnosticMessage: mailboxInfo.mailboxSmtpAddress };
            throw err;
        }
        const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
        return { accountStore, accountKey };
    });
};

export const getActivityFeedItem = (
    itemId: string,
    mailboxInfo: MailboxInfo
): ActivityFeedItem | undefined => {
    const accountStore = getActivityFeedAccountStore(mailboxInfo);
    const feedItem = accountStore.items.get(itemId);
    return feedItem;
};

let activityFeedMailboxesStore:
    | {
          mailboxes: MailboxInfo[];
          setMailboxes: (mailboxes: MailboxInfo[]) => void;
      }
    | undefined;

export const getActivityFeedMailboxesStore = (): {
    mailboxes: MailboxInfo[];
    setMailboxes: (mailboxes: MailboxInfo[]) => void;
} => {
    if (!activityFeedMailboxesStore) {
        activityFeedMailboxesStore = makeAutoObservable({
            mailboxes: [] as MailboxInfo[],
            setMailboxes(mailboxes: MailboxInfo[]) {
                this.mailboxes = mailboxes;
            },
        });
    }
    return activityFeedMailboxesStore;
};
